export const musicBotTags = ["MusicBot"] as const;

export interface MusicBotOverview {
    period?: string;
    users?: number;
    makers?: number;
    links?: number;
    clicks?: number;
    users_period?: number;
    makers_period?: number;
    links_period?: number;
    referred: number;
    in_channel: number;
}

export interface MusicBotListParams {
    limit?: number;
    offset?: number;
    kind?: string;
    status?: string;
    period?: string;
}

export interface MusicChannelPost {
    id: number;
    title: string;
    description: string;
    body: string;
    photo_path: string | null;
    photo_url: string | null;
    video_path?: string | null;
    audio_path?: string | null;
    has_cover?: boolean;
    has_ref_cover?: boolean;
    has_video?: boolean;
    has_audio?: boolean;
    video_name?: string | null;
    audio_name?: string | null;
    status: "draft" | "published";
    channel_message_id: number | null;
    published_at: string | null;
    created_at: string;
    updated_at: string;
    channel?: string;
    post_url?: string | null;
    ref_preview_title?: string;
    ref_preview_description?: string;
    /** msg id аудіо/дод. меседжів каналу → рефка на цей пост */
    alias_message_ids?: number[];
}

export const musicBotEndpoints = {
    getMusicBotOverview: {
        query: (params: { period?: string } = {}) => ({
            url: "/admin/music-bot/overview",
            params,
        }),
        providesTags: musicBotTags,
    },
    getMusicBotUsers: {
        query: (params: MusicBotListParams = {}) => ({
            url: "/admin/music-bot/users",
            params,
        }),
        providesTags: musicBotTags,
    },
    getMusicBotLinks: {
        query: (params: MusicBotListParams = {}) => ({
            url: "/admin/music-bot/links",
            params,
        }),
        providesTags: musicBotTags,
    },
    getMusicBotEvents: {
        query: (params: MusicBotListParams = {}) => ({
            url: "/admin/music-bot/events",
            params,
        }),
        providesTags: musicBotTags,
    },
    getMusicBotPosts: {
        query: (params: MusicBotListParams = {}) => ({
            url: "/admin/music-bot/posts",
            params,
        }),
        providesTags: musicBotTags,
    },
    getMusicBotPost: {
        query: (id: number) => ({ url: `/admin/music-bot/posts/${id}` }),
        providesTags: musicBotTags,
    },
    createMusicBotPost: {
        query: (body: {
            title?: string;
            description?: string;
            body?: string;
            photo_url?: string;
        }) => ({
            url: "/admin/music-bot/posts",
            method: "POST",
            body,
        }),
        invalidatesTags: musicBotTags,
    },
    updateMusicBotPost: {
        query: ({
            id,
            ...body
        }: {
            id: number;
            title?: string;
            description?: string;
            body?: string;
            photo_url?: string | null;
            alias_message_ids?: number[] | string;
        }) => ({
            url: `/admin/music-bot/posts/${id}`,
            method: "PATCH",
            body,
        }),
        invalidatesTags: musicBotTags,
    },
    deleteMusicBotPost: {
        query: (id: number) => ({
            url: `/admin/music-bot/posts/${id}`,
            method: "DELETE",
        }),
        invalidatesTags: musicBotTags,
    },
    hideMusicBotPost: {
        query: (id: number) => ({
            url: `/admin/music-bot/posts/${id}/hide`,
            method: "POST",
        }),
        invalidatesTags: musicBotTags,
    },
    uploadMusicBotPostPhoto: {
        query: ({ id, photo }: { id: number; photo: File }) => {
            const form = new FormData();
            form.append("photo", photo);
            return {
                url: `/admin/music-bot/posts/${id}/photo`,
                method: "POST",
                body: form,
            };
        },
        invalidatesTags: musicBotTags,
    },
    uploadMusicBotPostMedia: {
        query: ({
            id,
            kind,
            file,
            clear,
        }: {
            id: number;
            kind: "photo" | "video" | "audio" | "ref_photo";
            file?: File;
            clear?: boolean;
        }) => {
            const form = new FormData();
            form.append("kind", kind);
            if (clear) form.append("clear", "1");
            if (file) form.append("file", file);
            return {
                url: `/admin/music-bot/posts/${id}/media`,
                method: "POST",
                body: form,
            };
        },
        invalidatesTags: musicBotTags,
    },
    publishMusicBotPost: {
        query: (id: number) => ({
            url: `/admin/music-bot/posts/${id}/publish`,
            method: "POST",
        }),
        invalidatesTags: musicBotTags,
    },
    syncMusicBotPost: {
        query: (id: number) => ({
            url: `/admin/music-bot/posts/${id}/sync`,
            method: "POST",
        }),
        invalidatesTags: musicBotTags,
    },
    seriesMusicBotPost: {
        query: (body: {
            id: number;
            action: "attach" | "detach" | "make_main";
            main_id?: number;
        }) => ({
            url: `/admin/music-bot/posts/${body.id}/series`,
            method: "POST",
            body: {
                action: body.action,
                main_id: body.main_id,
            },
        }),
        invalidatesTags: musicBotTags,
    },
    importMusicBotPosts: {
        query: (body: { from_id?: number; to_id?: number; limit?: number } = {}) => ({
            url: "/admin/music-bot/posts/import",
            method: "POST",
            body,
        }),
        invalidatesTags: musicBotTags,
    },
};
